/**
 * Money math shared across every pricing engine.
 *
 * Single source of truth for the "round half-up to cents" rule
 * (DR-10168 Rule 2 / AC 2). Keeping it here means all calculators round
 * identically — no engine can drift by a cent from another.
 */
export class MoneyService {
  /**
   * Round half-up to cents.
   *
   * Every node in a pricing tree must resolve to a whole-cent value AT THE
   * MOMENT IT POSTS, so that parents sum children that are already whole cents
   * and roll-up addition is exact. Never sum unrounded values and round the
   * total — that reproduces the $1,609.15-vs-$1,609.14 drift the spec warns
   * about.
   *
   * The tiny sign-aware nudge counters binary floating-point error so values
   * that are mathematically on the half-cent (e.g. 1.005) round up instead of
   * being dragged down by a `100.49999999999999` representation.
   */
  public roundToCents(value: number): number {
    if (!Number.isFinite(value)) return 0;
    const cents = value * 100;
    return Math.round(cents + Math.sign(cents) * 1e-9) / 100;
  }
}

/** Shared singleton — inject this into calculators by default. */
export const moneyService = new MoneyService();
